import { __decorate, __metadata } from "tslib";
import { makeAutoObservable, runInAction } from 'mobx';
import { component, initialize } from 'tsdi';
import { injectTSDI } from 'ml-webclient-common/src/tsdi';
import { Config } from './config';
const INTL_BASE_URL = 'https://intl.sportalliance.com/fast-finance-link';
const translationLocales = [
    'de',
    'de_CH',
    'en',
    'en_GB',
    'it',
    'pl',
    'tr',
    'es',
    'fr',
    'nl',
    'cs',
    'sl',
    'hu',
    'ro',
    'nb',
    'pt'
];
function getLocaleForTranslations(locale) {
    const [language] = locale.split('-');
    const mappedLocale = locale.replace('-', '_');
    const exactMatch = translationLocales.find((supportedLocale) => supportedLocale === mappedLocale);
    const languageMatch = translationLocales.find((supportedLocale) => supportedLocale.startsWith(language));
    return exactMatch ?? languageMatch ?? 'en';
}
function isValidJsonResponse(response) {
    const contentType = response.headers.get('content-type');
    return Boolean(contentType?.includes('application/json'));
}
let I18nStore = class I18nStore {
    messages = {};
    locale = 'en';
    initialized = false;
    get config() {
        return injectTSDI(Config);
    }
    get currentLocale() {
        // Use navigator.language for Intl formatting (e.g. 'de-DE', 'en-GB', 'fr-FR')
        // This gives the most locale-specific formatting for dates, numbers, etc.
        return navigator.language;
    }
    get isReady() {
        return this.initialized;
    }
    init() {
        makeAutoObservable(this);
        this.locale = getLocaleForTranslations(this.config.getLocale());
        void this.loadRemoteTranslations();
    }
    async loadRemoteTranslations() {
        try {
            const response = await fetch(`${INTL_BASE_URL}/${this.locale}.json`);
            if (!response.ok || !isValidJsonResponse(response)) {
                this.initialized = true;
                return;
            }
            const remoteMessages = (await response.json());
            runInAction(() => {
                this.messages = remoteMessages;
                this.initialized = true;
            });
        }
        catch {
            runInAction(() => {
                this.initialized = true;
            });
        }
    }
    translate = (key, ...params) => {
        if (!key) {
            return '';
        }
        const message = this.messages[key];
        if (!message) {
            return key;
        }
        return this.interpolate(message, params);
    };
    __ = this.translate;
    has = (key) => {
        return Boolean(key && this.messages[key]);
    };
    interpolate(text, params) {
        if (!params.length) {
            return text;
        }
        // Handle {0}, {1}, ... style placeholders
        let result = text.replace(/\{(\d+)\}/g, (match, index) => {
            const paramIndex = parseInt(index, 10);
            const param = params[paramIndex];
            return param !== undefined ? String(param) : match;
        });
        // Handle %1$s, %2$s, ... Java-style placeholders (1-indexed)
        result = result.replace(/(%\d+\$s)/g, (match) => {
            const paramIndex = parseInt(match.replace('%', ''), 10) - 1;
            const param = params[paramIndex];
            return param !== undefined ? String(param) : match;
        });
        return result;
    }
    formatCurrency = (value) => {
        if (value === undefined) {
            return '-';
        }
        const amount = typeof value === 'number' ? value : value.amount;
        const currency = typeof value === 'number' ? 'EUR' : (value.currency ?? 'EUR');
        return amount.toLocaleString(this.currentLocale, {
            style: 'currency',
            currency
        });
    };
    formatDate = (value) => {
        if (!value) {
            return '-';
        }
        const date = value instanceof Date ? value : value.toDate();
        return date.toLocaleDateString(this.currentLocale, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };
    formatDateTime = (value) => {
        if (!value) {
            return '-';
        }
        const date = value instanceof Date ? value : value.toDate();
        return date.toLocaleString(this.currentLocale, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    formatTime = (value) => {
        if (!value) {
            return '-';
        }
        const date = value instanceof Date ? value : value.toDate();
        return date.toLocaleString(this.currentLocale, {
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    formatDecimal = (value, disableGrouping, decimalLimit) => {
        if (value === undefined) {
            return '-';
        }
        return value.toLocaleString(this.currentLocale, {
            minimumFractionDigits: decimalLimit ?? 0,
            maximumFractionDigits: decimalLimit ?? 2,
            useGrouping: !disableGrouping
        });
    };
    formatInteger = (value) => {
        if (value === undefined) {
            return '-';
        }
        return value.toLocaleString(this.currentLocale, {
            useGrouping: true
        });
    };
    get translationsReady() {
        return this.initialized;
    }
};
__decorate([
    initialize,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], I18nStore.prototype, "init", null);
I18nStore = __decorate([
    component()
], I18nStore);
export { I18nStore };
export function useI18n() {
    const i18n = injectTSDI(I18nStore);
    return {
        __: i18n.translate,
        formatMessage: (descriptor, params) => {
            const translated = i18n.translate(descriptor.id, ...(params ?? []));
            return translated === descriptor.id && descriptor.defaultMessage
                ? descriptor.defaultMessage
                : translated;
        },
        formatCurrency: i18n.formatCurrency,
        formatDate: i18n.formatDate,
        formatDateTime: i18n.formatDateTime,
        formatTime: i18n.formatTime,
        formatDecimal: i18n.formatDecimal,
        formatInteger: i18n.formatInteger,
        locale: i18n.currentLocale
    };
}
